import { CloseCircleOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client';
import {
  ErrorResponse,
  GET_IDEA_REVIEWER_LIST,
  IdeaReviewerList,
  SEND_IDEA_REVIEWER_REQUEST,
} from '@frontend/data-access';
import { UserAvatar } from '@frontend/shared/ui';
import { App, Button, Select } from 'antd';
import useTranslation from 'next-translate/useTranslation';
import { useState, useMemo, useEffect } from 'react';

function IdeaInviteReviewer({
  channelId,
  ideaId,
  onFinish,
}: {
  channelId: string;
  ideaId: string;
  onFinish: () => void;
}) {
  const { notification } = App.useApp();
  const { t } = useTranslation('common');
  const [inputValue, setInputValue] = useState();
  const [filteredReviewers, setFilteredReviewers] = useState<
    {
      value: number;
      label: JSX.Element;
    }[]
  >([]);

  const { data, loading, refetch } = useQuery<{
    ideaReviewerLists: {
      ideaReviewerListItem: IdeaReviewerList;
      requestStatus: boolean;
    }[];
  }>(GET_IDEA_REVIEWER_LIST, {
    fetchPolicy: 'no-cache',
    variables: {
      input: {
        channelId,
      },
    },
  });

  const renderReviewerItem = (item: {
    ideaReviewerListItem: IdeaReviewerList;
    requestStatus: boolean;
  }) => {
    return (
      <div className="flex items-center gap-2">
        <UserAvatar user={item.ideaReviewerListItem.user} size={24} />
        <div className="text-sm">
          <div className="text-gray-900">
            {item.ideaReviewerListItem.fullName}
          </div>
          <div className="text-gray-700">{item.ideaReviewerListItem.email}</div>
        </div>
      </div>
    );
  };

  const ideaReviewerList = useMemo(() => {
    if (!data?.ideaReviewerLists) return [];
    return data.ideaReviewerLists.filter(
      (item) => item.ideaReviewerListItem.user
    );
  }, [data?.ideaReviewerLists]);

  const reviewerOptions = useMemo(() => {
    if (!ideaReviewerList) return [];

    return ideaReviewerList
      .filter(
        (item, index, self) =>
          index ===
          self.findIndex(
            (t) =>
              t.ideaReviewerListItem.user.id ===
              item.ideaReviewerListItem.user.id
          )
      )
      .map((item) => ({
        value: item.ideaReviewerListItem.user.id,
        label: renderReviewerItem(item),
      }));
  }, [ideaReviewerList]);

  const handleError = (error: ErrorResponse) => {
    notification.error({
      message: t('error'),
      description: error.message,
      icon: (
        <span className="text-red-600">
          <CloseCircleOutlined />
        </span>
      ),
    });
  };

  const [sendInvite, { loading: inviteLoading }] = useMutation(
    SEND_IDEA_REVIEWER_REQUEST,
    {
      onCompleted: () => {
        setInputValue(undefined);
        onFinish();
      },
      onError: handleError,
      refetchQueries: [
        {
          query: GET_IDEA_REVIEWER_LIST,
          variables: {
            input: {
              channelId,
              ideaId,
            },
          },
        },
      ],
    }
  );

  const handleClickAdd = () => {
    sendInvite({
      variables: {
        input: {
          userId: inputValue,
          channelId,
          ideaId,
        },
      },
    });
  };

  useEffect(() => {
    refetch();
  }, [ideaId, channelId, refetch]);

  useEffect(() => {
    if (reviewerOptions.length > 0) {
      setFilteredReviewers(reviewerOptions);
    } else {
      setFilteredReviewers([]);
    }
  }, [reviewerOptions]);

  const setSearchValue = (value: string) => {
    if (!ideaReviewerList) {
      setFilteredReviewers([]);
      return;
    }

    if (!value.trim()) {
      setFilteredReviewers(reviewerOptions);
      return;
    }

    const filtered = ideaReviewerList.filter(
      (item) =>
        item.ideaReviewerListItem.fullName
          .toLowerCase()
          .includes(value.toLowerCase()) ||
        item.ideaReviewerListItem.email
          .toLowerCase()
          .includes(value.toLowerCase())
    );
    setFilteredReviewers(
      filtered.map((item) => ({
        value: item.ideaReviewerListItem.user.id,
        label: renderReviewerItem(item),
      }))
    );
  };

  return (
    <div className="flex items-center gap-2">
      <Select
        data-test="expert-search"
        loading={loading}
        value={inputValue}
        onChange={setInputValue}
        placeholder={t('select_reviewers')}
        options={filteredReviewers}
        className="w-full"
        size={inputValue ? 'large' : 'middle'}
        showSearch
        onSearch={setSearchValue}
        filterOption={false}
      />
      <Button
        shape="round"
        onClick={handleClickAdd}
        loading={inviteLoading}
        disabled={!inputValue}
        data-test="assign-btn"
      >
        {t('add')}
      </Button>
    </div>
  );
}

export default IdeaInviteReviewer;
